/**
 * StepIntent.js
 *
 * An onboarding step where users select their role or "intent" (e.g., Educator, Partner).
 * This selection determines the subsequent steps in the onboarding wizard.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import FormTemplate from '@/dialogs_common/SetupTemplate';
import Overlay from '@/forms/_common/Overlay';
import RequestError from '@/forms/_common/RequestError';
import { useOnboarding } from '@/pages/setup/account/OnboardingContext';
import IntentCheckboxList from './IntentCheckboxList';
import useTags from '@/hooks/useTags';
import TagTypes from '@/constants/TagTypes';
import _ from 'lodash';
import { PayloadStates } from '@lore/utils';


StepIntent.propTypes = {
  user: PropTypes.object,
};

/**
 * The intent selection step in the onboarding wizard.
 *
 * @param {object} _props - Component props.
 * @returns {JSX.Element} The rendered intent selection step.
 */
export default function StepIntent(_props) {
  const { updateData, registerOnBeforeNext, setNextDisabled, data, tagNodeCreate, tagNodeDestroy, refetchUser, saving, loading, error, request } = useOnboarding();

  // If initialSelected is empty, initialize it based on the user's intents
  const [selected, setSelected] = useState(() => {
    return data.selectedIntents || [];
  });

  useEffect(() => {
    if (selected.length === 0 && data.selectedIntents?.length > 0) {
      setSelected(data.selectedIntents);
    }
  }, [data.selectedIntents]);

  // Fetch tags for user intents (Educator, Organizer, Partner)
  const intentTags = useTags(TagTypes.Intent);

  useEffect(() => {
    setNextDisabled(selected.length === 0 || intentTags.state === PayloadStates.FETCHING);
  }, [selected, setNextDisabled, intentTags.state]);

  useEffect(() => {
    registerOnBeforeNext(async () => {
      updateData({ selectedIntents: selected });

      const educatorTag = _.find(intentTags.data, t => t.data.name === 'Educator');
      const organizerTag = _.find(intentTags.data, t => t.data.name === 'Organizer');
      const partnerTag = _.find(intentTags.data, t => t.data.name === 'Partner');

      const userIntents = data.intents || [];
      const userEducatorTagNode = _.find(userIntents, i => i.name === 'Educator');
      const userOrganizerTagNode = _.find(userIntents, i => i.name === 'Organizer');
      const userPartnerTagNode = _.find(userIntents, i => i.name === 'Partner');

      const promises = [];

      // School Educator
      if (selected.includes('school_educator')) {
        if (!userEducatorTagNode && educatorTag) {
          promises.push(tagNodeCreate({ tagId: educatorTag.id, userId: data.id || _props.user?.id }));
        }
      } else {
        if (userEducatorTagNode) {
          promises.push(tagNodeDestroy({ id: userEducatorTagNode.id, data: { id: userEducatorTagNode.id } }));
        }
      }

      // Organization Educator
      if (selected.includes('org_educator')) {
        if (!userOrganizerTagNode && organizerTag) {
          promises.push(tagNodeCreate({ tagId: organizerTag.id, userId: data.id || _props.user?.id }));
        }
      } else {
        if (userOrganizerTagNode) {
          promises.push(tagNodeDestroy({ id: userOrganizerTagNode.id, data: { id: userOrganizerTagNode.id } }));
        }
      }

      // Partner
      if (selected.includes('community_partner')) {
        if (!userPartnerTagNode && partnerTag) {
          promises.push(tagNodeCreate({ tagId: partnerTag.id, userId: data.id || _props.user?.id }));
        }
      } else {
        if (userPartnerTagNode) {
          promises.push(tagNodeDestroy({ id: userPartnerTagNode.id, data: { id: userPartnerTagNode.id } }));
        }
      }

      try {
        if (promises.length > 0) {
          await Promise.all(promises);
          await refetchUser();
        }
        return true;
      } catch (e) {
        console.error('Failed to update intent tags', e);
        return false;
      }
    });
  }, [selected, updateData, registerOnBeforeNext, intentTags.data, tagNodeCreate, tagNodeDestroy, refetchUser, data.id, data.intents, _props.user?.id]);

  /**
   * Toggles the selection of an intent.
   *
   * @param {string} id - The ID of the intent being toggled.
   */
  function toggle(id) {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    );
  }

  return (
    <div className="mt-6">
      <Overlay isVisible={loading || saving}>
        <FormTemplate
          title="Which of these statements best describe you? Choose one or more"
          subtitle="This helps us connect you to the right opportunities!"
          alert={error && (
            <RequestError request={request} v2={true} />
          )}
          body={<IntentCheckboxList selected={selected} onToggle={toggle} />}
        />
      </Overlay>
    </div>
  );
}
